import assert from 'assert';
import React from 'react';

import {CONSTANT_DOMAIN_LABEL_COLOR, VARIABLE_DOMAIN_LABEL_COLOR} from 'antibodies/AntibodyConstants';
import {
  AntibodyFormat,
  AntibodyFormatDomain,
  AntibodyFormatDomainVisualization,
  BlockDomainVisualization,
  ChainDomainSizing,
  ConnectorSizing,
  FusionDomainVisualization,
  HingeDomainVisualization,
  JoinerDomainVisualization,
  LinkerDomainVisualization,
  PathLinkerDomainVisualization,
  StraightLineLinkerDomainVisualization,
  SharedDomainParams,
} from 'antibodies/AntibodyTypedefs';
import {domainColor, isConstantDomain} from 'antibodies/AntibodyUtils';
import AntibodyDomain from 'antibodies/visualization/AntibodyDomain';
import AntibodyDomainLabel from 'antibodies/visualization/AntibodyDomainLabel';
import AntibodyFusionDomain from 'antibodies/visualization/AntibodyFusionDomain';
import AntibodyHinge from 'antibodies/visualization/AntibodyHinge';
import AntibodyJoiner from 'antibodies/visualization/AntibodyJoiner';
import AntibodyLinker from 'antibodies/visualization/AntibodyLinker';
import AntibodyPathLinker, {toPathParameters} from 'antibodies/visualization/AntibodyPathLinker';
import AntibodySiteMarker, {AntibodySiteMarkerProps} from 'antibodies/visualization/AntibodySiteMarker';
import AntibodyStraightLineLinker from 'antibodies/visualization/AntibodyStraightLineLinker';
import InteractiveComponentWrapper, {
  AntibodyInteractivityProps,
} from 'antibodies/visualization/InteractiveComponentWrapper';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import sortedBy from 'util/sortedBy';

/**
 * Given transform params, get the actual transform string that should be given to the SVG `g` wrapper for
 * this domain.
 * @param display.translate How far to move the domain, both horizontally and vertically
 * @param display.rotate How many degrees clockwise to rotate the domain
 * @param display.reflectX Whether to reflect the domain horizontally
 * @param display.reflectY Whether to reflect the domain vertically
 * @param hovered Whether the domain is hovered (scale up if so)
 */
function getDomainTransform(display: AntibodyFormatDomainVisualization, hovered: boolean = false): string {
  const {translate, rotate, reflectX, reflectY} = display;
  assert(!reflectY, 'reflectY is not supported yet');
  const translateX = translate.x;
  const translateY = translate.y;
  const translation = `translate(${translateX}, ${translateY})`;
  const rotation = `rotate(${rotate})`;
  let scale = '';
  if (hovered) {
    scale = reflectX ? 'scale(-1.04, 1.04)' : 'scale(1.04)';
  } else {
    scale = reflectX ? 'scale(-1, 1)' : '';
  }

  return [translation, rotation, scale].join(' ');
}

/**
 * Given transform params, get the actual transform string that should be given to the SVG `g` wrapper for
 * this domain label.
 *
 * Footgun alert: This function assumes that this transform is being applied to an element with SVG styles
 * of transform-box: fill-box and transform-origin: center. This is because SVG does not alter the transformed
 * element's own coordinate system, only that of the transformed element's children. There is no easy way to
 * rotate an element around its own center in SVG, so we do it with CSS instead.
 *
 * @param rotate How many degrees clockwise the domain was rotated. This rotation will be reversed for the
 *               label.
 * @param height How tall the domain is
 * @param width How wide the domain is
 */
function getLabelTransform(
  {rotate}: AntibodyFormatDomainVisualization,
  {height, width}: ChainDomainSizing
): string {
  const labelHeight = height / 4;
  const labelWidth = (3 * width) / 4;
  const translateX = width / 2 - labelWidth / 2;
  const translateY = height / 2 - labelHeight / 2;
  const translation = `translate(${translateX}, ${translateY})`;
  // Rotate the _opposite_ direction from the domain. We always want to render this text horizontally.
  const rotation = rotate != null ? `rotate(${-rotate})` : '';
  return [translation, rotation].join(' ');
}

/*
 * Returns whether a particular domain is active (hovered or selected) in the antibody map.
 * All domains referenced by a distinct chain are considered active, if the chain is hovered or selected
 * (because an active chain is reflected through updates to the visualization of its corresponding domains).
 */
export function isDomainActive({
  format,
  domainRef,
  interactedRef,
}: {
  format: AntibodyFormat;
  domainRef: number;
  interactedRef: number | null;
}): boolean {
  if (interactedRef == null) {
    return false;
  }

  // If the interacted component is a domain, all domains with the same type and specificity should be considered active
  const maybeFormatDomain = format.domainPositions.find((domain) => domain.ref === interactedRef);
  if (maybeFormatDomain != null) {
    const allMatchingDomainRefs = format.domainPositions
      .filter(
        (domain) =>
          domain.type === maybeFormatDomain.type && domain.specificity === maybeFormatDomain.specificity
      )
      .map((domain) => domain.ref);
    return allMatchingDomainRefs.includes(domainRef);
  }

  // If the active component is a chain, all domains on all chains with the same type and specificity should be considered active
  const formatChain = format.chains.find((chain) => chain.refs.includes(interactedRef));
  assert(formatChain != null, 'Interacted ref must be a valid component ref');
  return formatChain.domainRefsPerChain.flat().includes(domainRef);
}

function isDomainSelected({
  format,
  domainRef,
  interactivity,
}: {
  format: AntibodyFormat;
  domainRef: number;
  interactivity?: AntibodyInteractivityProps;
}): boolean {
  if (interactivity?.selectedRef == null) {
    return false;
  }
  return [interactivity.selectedRef, ...(interactivity.additionalSelectedRefs ?? [])].some((interactedRef) =>
    isDomainActive({format, domainRef, interactedRef})
  );
}

/**
 * Get whether the domain should be displayed as deemphasized.
 * @param format The format for the antibody.
 * @param domainRef Ref of domain in question.
 * @param interactivity Interactivity state for the antibody.
 */
function getDeemphasized({
  format,
  domainRef,
  interactivity,
}: {
  format: AntibodyFormat;
  domainRef: number;
  interactivity?: AntibodyInteractivityProps;
}): boolean {
  if (interactivity?.selectedRef != null) {
    return !isDomainSelected({format, domainRef, interactivity});
  }
  return (
    interactivity?.hoveredRef != null &&
    !isDomainActive({format, domainRef, interactedRef: interactivity?.hoveredRef})
  );
}

function safeGetChainIndex(ref: number, indexByRef: Record<number, number>): number {
  const index = indexByRef[ref];
  assert(index !== undefined, `Domain ref ${ref} not found in chains`);
  return index;
}

interface DomainWithLabel extends AntibodyFormatDomain {
  label: string;
}

/**
 * Gets the shared props for components that draw domains that are rendered as lines (hinges, linkers, joiners)
 */
function getSharedLineDomainProps(
  domain: DomainWithLabel,
  interactivity: AntibodyInteractivityProps | undefined,
  format: AntibodyFormat,
  indexByRef: Record<number, number>,
  connectorSizing: ConnectorSizing,
  showPopovers: boolean,
  antibodyId?: string
): {
  color: string;
  connectorSizing: ConnectorSizing;
} & SharedDomainParams {
  const {stroke} = domainColor({
    distinctDomain: {
      type: domain.type,
      chainIndex: safeGetChainIndex(domain.ref, indexByRef),
    },
    deemphasized: getDeemphasized({format, domainRef: domain.ref, interactivity}),
    selected: isDomainSelected({format, domainRef: domain.ref, interactivity}),
  });
  return {
    color: stroke,
    proteinId: interactivity?.proteinIdsByRef[domain.ref] ?? null,
    inaccessible: interactivity?.inaccessibleProteinIdsByRef[domain.ref] != null,
    label: domain.label,
    showPopovers,
    connectorSizing,
    antibodyId,
  };
}

export interface AntibodyProps {
  format: AntibodyFormat;
  interactivity?: AntibodyInteractivityProps;
  showLabels?: boolean;
  showPopovers?: boolean;
  antibodyId?: string;
  // Markers drawn on top of the glyph.
  siteMarkers?: Array<AntibodySiteMarkerProps>;
  showEmptyOptionalDomains?: boolean;
}

/**
 * An SVG visualization of an antibody.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * @param format The format for the antibody, which includes visualization data (display and sizing information).
 * @param interactivity Interactivity state for the antibody. If not passed, the antibody viz will be a static
 *                      SVG.
 * @param interactivity.hoveredRef Which domain ref is currently hovered, if any.
 * @param interactivity.proteinIdByRef A map from domain ref to associated protein API ID (including inaccessible ones).
 * @param interactivity.inaccessibleProteinIdsByRef A map from domain ref to each inaccessible protein API ID.
 * @param interactivity.selectedRef Which domain ref is currently selected, if any
 * @param interactivity.additionalSelectedRefs Other refs shown as selected along with selectedRef.
 * @param interactivity.setHoveredRef A function to set which domain ref is currently hovered.
 * @param interactivity.setSelectedRefs A function to set which domain ref is currently selected.
 * @param showLabels Whether to show labels on chain domains.
 * @param siteMarkers Markers to draw on top of the antibody, e.g. for conjugation sites.
 * @param showEmptyOptionalDomains Whether an interactive antibody draws optional domains that have no protein. Static
 *                                 antibodies always draw them.
 */
export default function Antibody({
  format,
  interactivity,
  showLabels,
  showPopovers = false,
  antibodyId,
  siteMarkers,
  showEmptyOptionalDomains = false,
}: AntibodyProps): JSX.Element {
  const domainLabelByRef: Record<number, string> = format.domains.reduce<Record<number, string>>(
    (acc, domain) => {
      for (const ref of domain.refs) {
        acc[ref] = domain.label;
      }
      return acc;
    },
    {}
  );
  const domainOptionalByRef: Record<number, boolean> = format.domains.reduce<Record<number, boolean>>(
    (acc, domain) => {
      for (const ref of domain.refs) {
        acc[ref] = domain.optional;
      }
      return acc;
    },
    {}
  );
  // Create a map of domains by ref for lookup
  const domainsByRef: Record<number, DomainWithLabel> = format.domainPositions.reduce<
    Record<number, DomainWithLabel>
  >((acc, domain) => {
    acc[domain.ref] = {...domain, label: domainLabelByRef[domain.ref]};
    return acc;
  }, {});

  const domainRefToDistinctChainIndex = format.chains.reduce<Record<number, number>>((acc, chain, idx) => {
    for (const domainRef of chain.domainRefsPerChain.flat()) {
      acc[domainRef] = idx;
    }
    return acc;
  }, {});

  // Group visualizations by type
  const hingeVisualizations: Array<HingeDomainVisualization> = [];
  const linkerVisualizations: Array<LinkerDomainVisualization> = [];
  const straightLineLinkerVisualizations: Array<StraightLineLinkerDomainVisualization> = [];
  const pathLinkerVisualizations: Array<PathLinkerDomainVisualization> = [];
  const joinerVisualizations: Array<JoinerDomainVisualization> = [];
  const chainDomainVisualizations: Array<BlockDomainVisualization | FusionDomainVisualization> = [];

  function shouldRenderRef(ref: number): boolean {
    if (interactivity == null || showEmptyOptionalDomains) {
      return true;
    }
    const isEmpty = interactivity.proteinIdsByRef[ref] == null;
    return !(domainOptionalByRef[ref] === true && isEmpty);
  }

  function getRenderableInteractedRef(ref: number | null): number | null {
    if (ref == null) {
      return null;
    }
    return shouldRenderRef(ref) ? ref : null;
  }

  const normalizedInteractivity =
    interactivity == null
      ? undefined
      : {
          ...interactivity,
          hoveredRef: getRenderableInteractedRef(interactivity.hoveredRef),
          selectedRef: getRenderableInteractedRef(interactivity.selectedRef),
          additionalSelectedRefs: interactivity.additionalSelectedRefs?.filter((ref) => shouldRenderRef(ref)),
        };

  for (const visualization of format.visualization.domainVisualizations) {
    if (!shouldRenderRef(visualization.ref)) {
      continue;
    }
    if (visualization.__typename === 'HingeDomainVisualizationNode') {
      hingeVisualizations.push(visualization);
    } else if (visualization.__typename === 'LinkerDomainVisualizationNode') {
      linkerVisualizations.push(visualization);
    } else if (visualization.__typename === 'StraightLineLinkerDomainVisualizationNode') {
      straightLineLinkerVisualizations.push(visualization);
    } else if (visualization.__typename === 'PathLinkerDomainVisualizationNode') {
      pathLinkerVisualizations.push(visualization);
    } else if (visualization.__typename === 'JoinerDomainVisualizationNode') {
      joinerVisualizations.push(visualization);
    } else if (
      visualization.__typename === 'BlockDomainVisualizationNode' ||
      visualization.__typename === 'FusionDomainVisualizationNode'
    ) {
      chainDomainVisualizations.push(visualization);
    }
  }

  // Sort chain domain visualizations by selected status. SVG z-indexing is a strict function of what is rendered first. As
  // such, anything with a stroke should be rendered last.
  const sortedChainDomainVisualizations = sortedBy(chainDomainVisualizations, [
    ({ref}) =>
      normalizedInteractivity?.selectedRef === ref ||
      normalizedInteractivity?.additionalSelectedRefs?.includes(ref)
        ? 1
        : -1,
  ]);

  const {chainDomainSizing, connectorSizing, svgSizing} = format.visualization.sizing;

  function getDomainLabel(domain: DomainWithLabel): string {
    // A bit of a hack to show an abbreviated version of the label
    // given a full-length label in the format "{domainType}{distinguisher}({chain})" eg "CH1A(Heavy)"
    const [labelWithoutChain, _] = domain.label.split('(');
    return labelWithoutChain;
  }

  return (
    <svg
      height="100%"
      width="100%"
      viewBox={`0 0 ${svgSizing.svgViewboxWidth} ${svgSizing.svgViewboxHeight}`}
    >
      {hingeVisualizations.map((display) => {
        const hinge = domainsByRef[display.ref];
        assert(hinge.type === AntibodyDomainType.H, 'Hinge display params must be for a hinge domain');
        const sharedProps = getSharedLineDomainProps(
          hinge,
          normalizedInteractivity,
          format,
          domainRefToDistinctChainIndex,
          connectorSizing,
          showPopovers,
          antibodyId
        );
        return (
          <g
            key={hinge.ref}
            transform={getDomainTransform(
              display,
              isDomainActive({
                format,
                domainRef: hinge.ref,
                interactedRef: normalizedInteractivity?.hoveredRef ?? null,
              })
            )}
            style={ROTATABLE_COMPONENT_STYLE}
          >
            <InteractiveComponentWrapper
              antibodyId={antibodyId}
              componentRef={hinge.ref}
              interactivity={normalizedInteractivity}
              componentLabel={sharedProps.label}
            >
              <AntibodyHinge domainVisualization={display} {...sharedProps} />
            </InteractiveComponentWrapper>
          </g>
        );
      })}
      {linkerVisualizations.map((display) => {
        const linker = domainsByRef[display.ref];
        assert(linker.type === AntibodyDomainType.L, 'Linker display params must be for a linker domain');
        const sharedProps = getSharedLineDomainProps(
          linker,
          normalizedInteractivity,
          format,
          domainRefToDistinctChainIndex,
          connectorSizing,
          showPopovers,
          antibodyId
        );
        return (
          <g
            key={linker.ref}
            transform={getDomainTransform(
              display,
              isDomainActive({
                format,
                domainRef: linker.ref,
                interactedRef: normalizedInteractivity?.hoveredRef ?? null,
              })
            )}
            style={ROTATABLE_COMPONENT_STYLE}
          >
            <InteractiveComponentWrapper
              antibodyId={antibodyId}
              componentRef={linker.ref}
              interactivity={normalizedInteractivity}
              componentLabel={sharedProps.label}
            >
              <AntibodyLinker domainVisualization={display} {...sharedProps} />
            </InteractiveComponentWrapper>
          </g>
        );
      })}
      {straightLineLinkerVisualizations.map((display) => {
        const straightLineLinker = domainsByRef[display.ref];
        assert(
          straightLineLinker.type === AntibodyDomainType.L ||
            straightLineLinker.type === AntibodyDomainType.ELEMENT,
          'Straight line linker display params must be for element or linker domains'
        );
        const sharedProps = getSharedLineDomainProps(
          straightLineLinker,
          normalizedInteractivity,
          format,
          domainRefToDistinctChainIndex,
          connectorSizing,
          showPopovers,
          antibodyId
        );
        return (
          <g
            key={straightLineLinker.ref}
            transform={getDomainTransform(
              display,
              isDomainActive({
                format,
                domainRef: straightLineLinker.ref,
                interactedRef: normalizedInteractivity?.hoveredRef ?? null,
              })
            )}
            style={ROTATABLE_COMPONENT_STYLE}
          >
            <InteractiveComponentWrapper
              antibodyId={antibodyId}
              componentRef={straightLineLinker.ref}
              interactivity={normalizedInteractivity}
              componentLabel={sharedProps.label}
            >
              <AntibodyStraightLineLinker domainVisualization={display} {...sharedProps} />
            </InteractiveComponentWrapper>
          </g>
        );
      })}
      {pathLinkerVisualizations.map((display) => {
        const pathLinker = domainsByRef[display.ref];
        assert(
          pathLinker.type === AntibodyDomainType.L,
          'Path linker display params must be for a path linker domain'
        );
        const sharedProps = getSharedLineDomainProps(
          pathLinker,
          normalizedInteractivity,
          format,
          domainRefToDistinctChainIndex,
          connectorSizing,
          showPopovers,
          antibodyId
        );
        const pathParameters = toPathParameters(display.pathParameters);
        return (
          <g
            key={pathLinker.ref}
            transform={getDomainTransform(
              display,
              isDomainActive({
                format,
                domainRef: pathLinker.ref,
                interactedRef: normalizedInteractivity?.hoveredRef ?? null,
              })
            )}
            style={ROTATABLE_COMPONENT_STYLE}
          >
            <InteractiveComponentWrapper
              antibodyId={antibodyId}
              componentRef={pathLinker.ref}
              interactivity={normalizedInteractivity}
              componentLabel={sharedProps.label}
            >
              <AntibodyPathLinker pathParameters={pathParameters} {...sharedProps} />
            </InteractiveComponentWrapper>
          </g>
        );
      })}
      {joinerVisualizations.map((display) => {
        const joiner = domainsByRef[display.ref];
        assert(joiner.type === AntibodyDomainType.J, 'Joiner display params must be for a joiner domain');
        const sharedProps = getSharedLineDomainProps(
          joiner,
          normalizedInteractivity,
          format,
          domainRefToDistinctChainIndex,
          connectorSizing,
          showPopovers,
          antibodyId
        );
        return (
          <g
            key={joiner.ref}
            transform={getDomainTransform(
              display,
              isDomainActive({
                format,
                domainRef: joiner.ref,
                interactedRef: normalizedInteractivity?.hoveredRef ?? null,
              })
            )}
            style={ROTATABLE_COMPONENT_STYLE}
          >
            <InteractiveComponentWrapper
              antibodyId={antibodyId}
              componentRef={joiner.ref}
              interactivity={normalizedInteractivity}
              componentLabel={sharedProps.label}
            >
              <AntibodyJoiner domainVisualization={display} {...sharedProps} />
            </InteractiveComponentWrapper>
          </g>
        );
      })}
      {sortedChainDomainVisualizations.map((display) => {
        const domain = domainsByRef[display.ref];
        assert(
          domain.type !== AntibodyDomainType.L &&
            domain.type !== AntibodyDomainType.H &&
            domain.type !== AntibodyDomainType.J,
          'Block domain display params must be for a chain domain'
        );
        const {fill, stroke} = domainColor({
          distinctDomain: {
            type: domain.type,
            chainIndex: safeGetChainIndex(domain.ref, domainRefToDistinctChainIndex),
          },
          deemphasized: getDeemphasized({
            format,
            domainRef: domain.ref,
            interactivity: normalizedInteractivity,
          }),
          selected: isDomainSelected({format, domainRef: domain.ref, interactivity: normalizedInteractivity}),
        });
        const sharedParams = {
          chainDomainSizing,
          fillColor: fill,
          strokeColor: stroke,
          proteinId: normalizedInteractivity?.proteinIdsByRef[domain.ref] ?? null,
          inaccessible: normalizedInteractivity?.inaccessibleProteinIdsByRef[domain.ref] != null,
          label: domain.label,
          showPopovers,
          antibodyId,
        };
        let domainComponent: JSX.Element;
        if (display.__typename === 'BlockDomainVisualizationNode') {
          domainComponent = <AntibodyDomain domainVisualization={display} {...sharedParams} />;
        } else if (display.__typename === 'FusionDomainVisualizationNode') {
          domainComponent = <AntibodyFusionDomain {...sharedParams} />;
        } else {
          assert(false, 'Unknown domain visualization type');
        }
        return (
          <g
            key={domain.ref}
            transform={getDomainTransform(
              display,
              isDomainActive({
                format,
                domainRef: domain.ref,
                interactedRef: normalizedInteractivity?.hoveredRef ?? null,
              })
            )}
            style={ROTATABLE_COMPONENT_STYLE}
          >
            <InteractiveComponentWrapper
              antibodyId={antibodyId}
              componentRef={domain.ref}
              interactivity={normalizedInteractivity}
              componentLabel={sharedParams.label}
            >
              {domainComponent}
              {showLabels && (
                <g transform={getLabelTransform(display, chainDomainSizing)} style={LABEL_WRAPPER_STYLE}>
                  <AntibodyDomainLabel
                    color={
                      isConstantDomain(domain.type)
                        ? CONSTANT_DOMAIN_LABEL_COLOR
                        : VARIABLE_DOMAIN_LABEL_COLOR
                    }
                    height={chainDomainSizing.height / 4}
                    text={getDomainLabel(domain)}
                    width={(3 * chainDomainSizing.width) / 4}
                  />
                </g>
              )}
            </InteractiveComponentWrapper>
          </g>
        );
      })}
      {/* Site markers are drawn last so they sit on top of the glyph. */}
      {siteMarkers?.map((marker, index) => (
        // Markers can be identical (e.g. the same site in two conjugations), so the index is in the key.
        <AntibodySiteMarker key={index} {...marker} />
      ))}
    </svg>
  );
}

// Inline rather than class-based so the styles survive a static server-side render.
const LABEL_WRAPPER_STYLE: React.CSSProperties = {
  transformBox: 'fill-box',
  transformOrigin: 'center',
};
// Rotate components around their own center, not the origin of the SVG.
const ROTATABLE_COMPONENT_STYLE: React.CSSProperties = {
  transformOrigin: 'center',
  transformBox: 'content-box',
};
